/* eslint-disable no-underscore-dangle */
import { BootFileParams } from '@quasar/app-webpack';
import { useNotifications } from 'SharedDir/common/composables/useNotifications';
import { HTTP_CODE_ERROR } from '@packages/toolbox/dist/constants/http-codes';
import { AUTHORIZATION } from 'SharedDir/backend/endpoints/access';
import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from 'SharedDir/stores/auth.store';
import { useUserStore } from 'SharedDir/stores/user.store';
import { RouteLocationRaw } from 'vue-router';
import { getWAFSupportID, soundsLikeWAF } from '@packages/toolbox';
import { useNotificationStore } from 'SharedDir/stores/notification.store';

const loginUrls = [AUTHORIZATION.LOGIN.url, AUTHORIZATION.ESIA_LOGIN.url];

type ExtendedInternalAxiosRequestConfig = InternalAxiosRequestConfig & { _retry: boolean };

type PendingRequest = {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
};

/**
 * Создаёт обработчик ошибок ответа для конкретного экземпляра HTTP-клиента.
 *
 * Очередь запросов, ожидающих обновления токена, живёт в замыкании экземпляра, а не на уровне модуля:
 * на SSR-сервере экземпляр свой у каждого запроса, поэтому очереди разных пользователей не пересекаются.
 *
 * @param http экземпляр, через который повторяются запросы после обновления токена
 * @param context контекст boot-файла: store для Pinia и router для редиректа после разлогина по 401
 * @param afterLogout куда вести пользователя после разлогина
 */
export const createErrorInterceptor = <TState>(
  http: AxiosInstance,
  context: BootFileParams<TState>,
  afterLogout: (redirectTo: string) => RouteLocationRaw | Promise<RouteLocationRaw>
) => {
  let isRefreshing = false;
  let failedQueue: Array<PendingRequest> = [];

  const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((pending) => {
      if (error) {
        pending.reject(error);
      } else {
        pending.resolve(token);
      }
    });

    failedQueue = [];
  };

  return async (error: AxiosError) => {
    const needErrorHandleInterceptors = error?.config?.headers?.interceptors ?? true;
    if (!needErrorHandleInterceptors) {
      return Promise.reject(error);
    }

    // На сервере ошибку только пробрасываем: обновлять токен и уводить на страницу входа
    // во время рендера нельзя, этим займётся клиент после гидрации
    if (process.env.SERVER) {
      return Promise.reject(error);
    }

    const notify = useNotifications();
    const authStore = useAuthStore(context.store);
    const userStore = useUserStore(context.store);
    const notificationStore = useNotificationStore(context.store);

    const logout = async () => {
      await authStore.logout();
      userStore.clearUser();
      console.debug('Logout from interceptor ...');
      const currentRoute = context.router.currentRoute.value;
      await context.router.push(await afterLogout(currentRoute.fullPath));
    };

    const originalRequest = error.config as ExtendedInternalAxiosRequestConfig;

    // Если нет соединения с сервером
    if (typeof error.response === 'undefined') {
      const message = 'Ошибка соединения с сервером, через несколько минут всё заработает!';
      notify.failureNotification(message);

      return Promise.reject(error);
    }

    /**
     * В данное условие попадает только запрос, который пошёл за токеном, но сам получил 401 ошибку
     * В таком случае гарантированно делаем разлогин пользователя и перекидываем на главную страницу
     */
    if (originalRequest.url === AUTHORIZATION.REFRESH_TOKEN.url) {
      processQueue(true, null);
      isRefreshing = false;
      await logout();
      return Promise.reject(error);
    }

    /**
     * Обработка 429 Too Many Requests («слишком много запросов»)
     */
    if (error.response.status === HTTP_CODE_ERROR.MANY_REQUESTS) {
      const message =
        'Превышено допустимое количество запросов. Пожалуйста, повторите через несколько минут!';
      notify.failureNotification(message);
    }

    if (soundsLikeWAF(error as any)) {
      const content = error.response.data as string;
      const payload = { id: getWAFSupportID(content), content, url: error.request.url };
      notificationStore.setWafError(payload);
      return error;
    }

    /**
     * Обработка 401 ошибки Unauthorized («не авторизован (не представился)») и вызывался не метод авторизации
     *
     * Пытаемся выполнить переавторизацию пользователя, если ошибка возникла не при вызове запроса на вторизацию
     */
    if (
      error.response.status === HTTP_CODE_ERROR.UNAUTHORIZED &&
      !loginUrls.includes(error.request.responseURL) &&
      !originalRequest._retry
    ) {
      /**
       * Проверка используемая для того, чтобы только первый запрос, получивший 401 ошибку ходил за новым токеном
       * Остальные запросы встают в очередь и ждут, когда первый запрос вернется с новым токеном и достанет их из очереди с использованием
       * функции processQueue
       */
      if (isRefreshing) {
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return http(originalRequest);
        });
      }

      originalRequest._retry = true;
      // Флаг, сообщающий, что за рефреш токеном уже отправились
      isRefreshing = true;

      /**
       * Выполнение переавторизации с сохранением нового токена
       * и запуском функции, достающей из очереди запросы, которые ожидают обновления рефреш токена
       */
      return new Promise((resolve, reject) => {
        authStore
          .doRefreshToken()
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(http(originalRequest));
            processQueue(null, token);
          })
          .catch(async (err) => {
            processQueue(err, null);
            await logout();
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  };
};
